/* ===========================================
   GET JOY - GWP FREE TREAT LOGIC
   =========================================== */

(function() {
  'use strict';

  // Default config (overwritten by metaobject)
  let GWP_CONFIG = {
    active: false,
    promoCode: '',
    giftVariantId: null,
    giftSellingPlan: null,
    giftProductTitle: '',
    giftProductPrice: 0,
    giftProductImage: '',
    promoStartDate: null,
    promoEndDate: null,
    giftDurationMonths: 12,
    // Internal keys
    STORAGE_KEY: 'getjoy_gwp_eligible',
    GWP_PROPERTY_KEY: '_gwp_promo'
  };

  // Load config from metaobject (injected by Liquid section)
  function loadConfig() {
    const configElement = document.getElementById('gwp-config-data');
    if (!configElement) {
      console.warn('[GWP] Config element not found');
      return false;
    }

    try {
      const metaobjectConfig = JSON.parse(configElement.textContent);
      GWP_CONFIG = { ...GWP_CONFIG, ...metaobjectConfig };
      console.log('[GWP] Config loaded:', GWP_CONFIG);
      return GWP_CONFIG.active;
    } catch (error) {
      console.error('[GWP] Failed to parse config:', error);
      return false;
    }
  }

  // Check if promo is within date range
  function isPromoActive() {
    if (!GWP_CONFIG.active) return false;

    const now = new Date();

    if (GWP_CONFIG.promoStartDate) {
      const start = new Date(GWP_CONFIG.promoStartDate);
      if (now < start) return false;
    }

    if (GWP_CONFIG.promoEndDate) {
      const end = new Date(GWP_CONFIG.promoEndDate);
      end.setHours(23, 59, 59, 999); // Include full end day
      if (now > end) return false;
    }

    return true;
  }

  // Detect promo from URL
  // Supports both ?discount=CODE and /discount/CODE formats
  function detectPromoUrl() {
    let discountCode = null;

    // Check URL parameter format: ?discount=CODE
    const urlParams = new URLSearchParams(window.location.search);
    discountCode = urlParams.get('discount');

    // Check path format: /discount/CODE (may not work due to server redirect)
    if (!discountCode) {
      const pathMatch = window.location.pathname.match(/\/discount\/([^\/\?]+)/i);
      if (pathMatch) {
        discountCode = decodeURIComponent(pathMatch[1]);
      }
    }

    if (discountCode?.toUpperCase() === GWP_CONFIG.promoCode.toUpperCase()) {
      if (isPromoActive()) {
        localStorage.setItem(GWP_CONFIG.STORAGE_KEY, 'true');
        console.log('[GWP] Promo eligibility set from URL:', discountCode);
      } else {
        console.log('[GWP] Promo code detected but promo not active');
      }
    }
  }

  // Check if promo code is applied to cart (handles /discount/CODE redirects)
  async function detectPromoFromCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Check cart-level discount codes
      if (cart.discount_codes && cart.discount_codes.length > 0) {
        const hasPromoCode = cart.discount_codes.some(
          discount => discount.code?.toUpperCase() === GWP_CONFIG.promoCode.toUpperCase()
        );

        if (hasPromoCode && isPromoActive()) {
          localStorage.setItem(GWP_CONFIG.STORAGE_KEY, 'true');
          console.log('[GWP] Promo eligibility set from cart discount');
          return true;
        }
      }

      // Also check cart attributes (some discount apps store it here)
      if (cart.attributes?.discount_code?.toUpperCase() === GWP_CONFIG.promoCode.toUpperCase()) {
        if (isPromoActive()) {
          localStorage.setItem(GWP_CONFIG.STORAGE_KEY, 'true');
          console.log('[GWP] Promo eligibility set from cart attributes');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.log('[GWP] Could not check cart for discount:', error);
      return false;
    }
  }

  // Check eligibility
  function isGwpEligible() {
    return localStorage.getItem(GWP_CONFIG.STORAGE_KEY) === 'true' && isPromoActive();
  }

  function cartHasSubscriptionProduct(cart) {
    // Check for subscription products, excluding the GWP item itself
    const subscriptionItems = cart.items.filter(item => {
      const isGwpItem = item.properties?.[GWP_CONFIG.GWP_PROPERTY_KEY];
      // selling_plan_allocation can be null OR undefined for one-time purchases
      const hasSubscription = item.selling_plan_allocation != null;
      return hasSubscription && !isGwpItem;
    });

    console.log('[GWP] Cart items check:', cart.items.map(item => ({
      title: item.title,
      variant_id: item.variant_id,
      selling_plan_allocation: item.selling_plan_allocation,
      isGwp: !!item.properties?.[GWP_CONFIG.GWP_PROPERTY_KEY]
    })));
    console.log('[GWP] Subscription items found:', subscriptionItems.length);

    return subscriptionItems.length > 0;
  }

  function getTreatInCart(cart) {
    return cart.items.find(item =>
      item.variant_id == GWP_CONFIG.giftVariantId &&
      item.properties?.[GWP_CONFIG.GWP_PROPERTY_KEY]
    );
  }

  // Add treat to cart
  async function addFreeTreatToCart(cart) {
    if (!GWP_CONFIG.giftVariantId) {
      console.error('[GWP] No gift variant ID configured');
      return;
    }

    if (getTreatInCart(cart)) {
      console.log('[GWP] Treat already in cart');
      return;
    }

    const variantId = parseInt(GWP_CONFIG.giftVariantId, 10);

    const payload = {
      id: variantId,
      quantity: 1,
      properties: {
        [GWP_CONFIG.GWP_PROPERTY_KEY]: GWP_CONFIG.promoCode,
        '_message': 'Free with your subscription!'
      }
    };

    // Add selling plan if configured
    if (GWP_CONFIG.giftSellingPlan) {
      payload.selling_plan = parseInt(GWP_CONFIG.giftSellingPlan, 10);
    }

    console.log('[GWP] Adding treat with payload:', payload);

    try {
      let response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      let data = await response.json();

      // If failed and no selling plan was provided, try to fetch product and get selling plan
      if (!response.ok && !payload.selling_plan) {
        console.log('[GWP] First attempt failed, trying to fetch product selling plan...');

        const sellingPlan = await fetchProductSellingPlan(variantId);
        if (sellingPlan) {
          payload.selling_plan = sellingPlan;
          console.log('[GWP] Retrying with selling plan:', sellingPlan);

          response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          data = await response.json();
        }
      }

      if (!response.ok) {
        console.error('[GWP] Cart add error response:', data);
        throw new Error(data.description || data.message || 'Failed to add treat');
      }

      console.log('[GWP] Free treat added to cart:', data);
      refreshCart();

    } catch (error) {
      console.error('[GWP] Failed to add free treat:', error);
    }
  }

  // Fetch product data to get selling plan ID
  async function fetchProductSellingPlan(variantId) {
    try {
      // Try to get product info using the product handle from config
      const productHandle = GWP_CONFIG.giftProductHandle;
      if (!productHandle) {
        console.log('[GWP] No product handle configured');
        return null;
      }

      const response = await fetch(`/products/${productHandle}.js`);

      if (!response.ok) {
        console.log('[GWP] Could not fetch product data');
        return null;
      }

      const product = await response.json();
      console.log('[GWP] Product data:', product);

      // Find the variant
      const variant = product.variants.find(v => v.id === variantId);
      if (variant) {
        console.log('[GWP] Found variant:', variant);
      } else {
        console.log('[GWP] Variant not found in product. Available variants:', product.variants.map(v => ({ id: v.id, title: v.title })));
      }

      // Check if product has selling plan groups (filter by app_id SKIO)
      // edited by Skio
      const skioGroups = (product.selling_plan_groups || []).filter(g => g.app_id === 'SKIO');
      if (skioGroups.length > 0) {
        const firstGroup = skioGroups[0];
        if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
          const sellingPlanId = firstGroup.selling_plans[0].id;
          console.log('[GWP] Found selling plan from product:', sellingPlanId);
          return sellingPlanId;
        }
      }

      return null;
    } catch (error) {
      console.log('[GWP] Error fetching product selling plan:', error);
      return null;
    }
  }

  // Remove treat from cart
  async function removeFreeTreatFromCart(cart) {
    const treatItem = getTreatInCart(cart);
    if (!treatItem) {
      console.log('[GWP] No treat to remove');
      return;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: treatItem.key,
          quantity: 0
        })
      });

      if (!response.ok) throw new Error('Failed to remove treat');

      console.log('[GWP] Free treat removed from cart');
      refreshCart();

    } catch (error) {
      console.error('[GWP] Failed to remove free treat:', error);
    }
  }

  // Main evaluation
  let isEvaluating = false;

  async function evaluateGwp() {
    if (isEvaluating) {
      console.log('[GWP] Already evaluating, skipping');
      return;
    }
    if (!GWP_CONFIG.active || !GWP_CONFIG.giftVariantId) {
      console.log('[GWP] Config not ready, skipping evaluation');
      return;
    }

    isEvaluating = true;
    console.log('[GWP] Starting evaluation...');

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const hasTreat = getTreatInCart(cart);
      const hasSubscription = cartHasSubscriptionProduct(cart);
      const isEligible = isGwpEligible();
      const shouldHaveTreat = isEligible && hasSubscription;

      console.log('[GWP] Evaluation result:', {
        hasTreat: !!hasTreat,
        hasSubscription,
        isEligible,
        shouldHaveTreat
      });

      if (shouldHaveTreat && !hasTreat) {
        console.log('[GWP] Adding treat...');
        await addFreeTreatToCart(cart);
      } else if (!shouldHaveTreat && hasTreat) {
        console.log('[GWP] Removing treat...');
        await removeFreeTreatFromCart(cart);
      } else {
        console.log('[GWP] No action needed');
      }

    } catch (error) {
      console.error('[GWP] Evaluation failed:', error);
    } finally {
      isEvaluating = false;
    }
  }

  // Cart refresh - uses Get Joy theme events
  function refreshCart() {
    // Method 1: Custom events used by Get Joy theme
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

    // Method 2: Fetch and update cart count display
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble span, .cart__count')
          .forEach(el => el.textContent = cart.item_count);
      });
  }

  // Prevent manual modification of GWP item
  function setupProtection() {
    // Prevent quantity changes
    document.addEventListener('change', function(e) {
      const gwpItem = e.target.closest('[data-gwp-item]');
      if (gwpItem && (e.target.type === 'number' || e.target.tagName === 'SELECT')) {
        e.preventDefault();
        e.stopPropagation();
        e.target.value = 1;
        return false;
      }
    }, true);

    // Prevent removal
    document.addEventListener('click', function(e) {
      const gwpItem = e.target.closest('[data-gwp-item]');
      const isRemoveButton = e.target.closest('[data-remove], .cart-item__remove, .remove, [href*="/cart/change"], [data-action="remove-cart-item"]');

      if (gwpItem && isRemoveButton) {
        e.preventDefault();
        e.stopPropagation();
        console.log('[GWP] Cannot manually remove free treat');
        return false;
      }
    }, true);
  }

  // Initialize
  async function init() {
    const isActive = loadConfig();

    if (!isActive) {
      console.log('[GWP] Promo not active');
      return;
    }

    // Check URL first, then check cart for applied discount (handles /discount/CODE redirects)
    detectPromoUrl();
    await detectPromoFromCart();

    setupProtection();
    evaluateGwp();

    // Listen for cart changes - Get Joy theme events
    // Use longer delays to ensure cart is fully updated before evaluation
    document.addEventListener('cart:updated', () => setTimeout(evaluateGwp, 300));
    document.addEventListener('cart:change', () => setTimeout(evaluateGwp, 300));
    document.addEventListener('cart:build', () => setTimeout(evaluateGwp, 500));
    document.addEventListener('ajaxProduct:added', () => setTimeout(evaluateGwp, 300));
    document.addEventListener('cart:refresh', () => setTimeout(evaluateGwp, 300));

    // Listen for subscription toggle changes in cart drawer
    document.addEventListener('change', function(e) {
      if (e.target.dataset?.toggles === 'line-autoship-options' ||
          e.target.dataset?.toggles === 'line-frequency-update' ||
          e.target.classList?.contains('toggle-checkbox')) {
        // Longer delay to allow cart API calls to complete
        setTimeout(evaluateGwp, 800);
      }
    });

    // Fallback: re-evaluate periodically when cart drawer opens
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-cart-toggle], .cart-toggle, [href="/cart"], .js-drawer-open-cart, [data-drawer-toggle="cart"]')) {
        setTimeout(evaluateGwp, 300);
      }
    });
  }

  // Start when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.GWP_DEBUG = {
    getConfig: () => GWP_CONFIG,
    evaluate: evaluateGwp,
    isEligible: isGwpEligible,
    isPromoActive: isPromoActive,
    setEligible: (value) => {
      if (value) {
        localStorage.setItem(GWP_CONFIG.STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(GWP_CONFIG.STORAGE_KEY);
      }
      console.log('[GWP] Eligibility manually set to:', value);
    }
  };

})();
